<template>
    <div class="page-content">
        <UserHeadline :title="$t('Summary.title')" />
        <div class="page-content__container">
            <div class="graphs" v-if="$mq.includes('desktop_xxxl')">
                <div class="graphs__year-select">
                    <TotemSelect
                        :options="getDateRanges"
                        :placeholder="$t('Summary.select_graph_year')"
                        @input="selectDateRange"
                        ref="selectedDateRange"
                        :value="selectedYear"
                        :reduce="option => option.value"
                        showSelectedOption
                    />
                </div>
                <div :class="['graphs__container', {'align-center': $isLangDk }]">
                    <BarGraph 
                        :completedCourses="completedCourses"
                        :unfinishedCourses="unfinishedCourses" 
                        :selectedYear="selectedYear"
                    />
                    <PolarAreaGraph 
                        :completedCourses="completedCourses" 
                        :selectedYear="selectedYear"
                    />
                    <BarGraphAuthorizations
                        :completedCourses="completedCourses"
                        :unfinishedCourses="unfinishedCourses"
                        :selectedYear="selectedYear"
                        v-if="!$isLangDk"
                    />
                </div>
            </div>
            <div class="summary">
                <div class="summary__inprogress-courses" v-if="limitedUnfinishedCourses.length > 0">
                    <div class="summary__action">
                        <h2 class="user-page" v-html="$t('Summary.incomplete_training')"></h2>
                        <TotemButton
                            class="summary__completed-courses-button"
                            size="small"
                            color="secondary"
                            @click="goToUncompletedCourses"
                            :label="$t('Summary.open_uncompleted')"
                            icon="arrow-right"
                            iconPosition="right"
                        />
                    </div>
                    <CarouselCourses :courses="limitedUnfinishedCourses" theme="dark" />
                </div>

                <div class="summary__completed-courses" v-if="limitedCompletedCourses.length > 0">
                    <div class="summary__action">
                        <h2 class="user-page">
                            <span v-html="$t('Summary.recent_training')"></span>
                        </h2>
                        <TotemButton
                            class="summary__completed-courses-button"
                            size="small"
                            color="secondary"
                            @click="goToCompletedCourses"
                            :label="$t('Summary.open_completed')"
                            icon="arrow-right"
                            iconPosition="right"
                        />
                    </div>

                    <CarouselCourses :courses="limitedCompletedCourses" theme="dark">
                        <template v-slot="swiper">
                            <TotemCourseItemMosaic :course="swiper.course" :isCompletedView="true"/>
                        </template>
                    </CarouselCourses>
                </div>
            </div>
        </div>
        <ModalAddToNewWatchlist />
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import UserHeadline from 'components/user/UserHeadline'
    import CarouselCourses from 'components/user/summary/carousel/CarouselCourses'
    import BarGraph from 'components/user/summary/BarGraph'
    import BarGraphAuthorizations from 'components/user/summary/BarGraphAuthorizations'
    import PolarAreaGraph from 'components/user/summary/PolarAreaGraph'
    import ModalAddToNewWatchlist from 'components/shared/modals/ModalAddToNewWatchlist'
    import loadingOverlay from 'components/shared/mixins/loadingOverlay'

    export default {
        name: 'Summary',
        components: {
            UserHeadline,
            CarouselCourses,
            BarGraph,
            BarGraphAuthorizations,
            PolarAreaGraph,
            ModalAddToNewWatchlist
        },
        mixins: [ loadingOverlay ],
        data () {
            return {
                limitedRandomCourses: [],
                limit: 15
            }
        },
        computed: {
            ...mapGetters({
                courses: 'Courses/getCoursesList',
                certificates: 'CompletedCourses/certificates',
                coursesForVideoLogs: 'VideoLog/getCoursesForVideoLogs',
                isLoggedIn: 'isLoggedIn',
                selectedYear: 'User/getGraphYear'
            }),
            completedCourses () {
                if (!this.certificates || this.certificates.length === 0) {
                    return []
                }
                return this.certificates.courses
            },
            unfinishedCourses () {
                return this.coursesForVideoLogs.filter((course) => {
                    return this.completedCourses.findIndex(el => el.id === course.id) === -1
                })
            },
            limitedUnfinishedCourses () {
                return this.unfinishedCourses.filter((course, index) => index < this.limit && !this.isCourseHidden(course))
            },
            limitedCompletedCourses () {
                if (!this.completedCourses || this.completedCourses.length === 0) {
                    return []
                }
                return this.completedCourses.filter((course, index) => index < this.limit)
            },
            getDateRanges () {
                if (!this.completedCourses || this.completedCourses.length === 0) {
                    return []
                }
                const yearsSet = this.completedCourses
                    .reduce((years, course) => {
                        years.add(course.certificate.certificationDate?.substr(0, 4))
                        return years
                    }, new Set())
                const options = Array.from(yearsSet).map(year => ({ label: year, value: year }))
                options.push({ label: this.$t('BarGraph.select_last_twelve_months'), value: 'last-12-months' })
                return options
            }
        },
        methods: {
            goToCompletedCourses () {
                this.$router.push({ name: 'user-completed' })
            },
            goToUncompletedCourses () {
                this.$router.push({ name: 'user-uncompleted' })
            },
            async refreshPackage () {
                const taskName = 'loadingSummary'
                try {
                    await this.$store.dispatch('LoaderScreen/addTaskToLoader', {
                        key: taskName,
                        message: 'Loading Summary'
                    })
                    await this.$store.dispatch('User/loadRecommendedCourses')
                } catch (e) {
                }
                this.$store.dispatch('LoaderScreen/removeTaskFromLoader', taskName)
            },
            selectDateRange (option) {
                this.$store.dispatch('User/setSummaryGraphYear', option)
            },
            isCourseHidden (course) {
                return course.userMetadata && course.userMetadata.hiddenInUnfinishedCoursesList
            }
        },
        async created () {
            await this.$store.dispatch('CompletedCourses/loadCertificates')
            await this.$store.dispatch('VideoLog/loadVideoLogs')
        },
        watch: {
            isLoggedIn: {
                handler: 'refreshPackage',
                immediate: true
            }
        }
    }
</script>

<style lang="scss" scoped>
.graphs {
    display: flex;
    justify-content: center;
    padding: 30px;
    border: 1px solid $alto;
    margin: 0 0 50px;
    flex-direction: column;

    &__year-select {
        margin: 0 0 15px 0;
    }

    &__container {
        display: flex;
        flex-direction: column;
        gap: 60px;

        [class*="desktop"] & {
            flex-direction: row;
        }
    }
}

.summary {
    display: flex;
    flex-direction: column;

    &__action {
        display: flex;
        margin: 0 0 20px;
        align-items: center;
        gap: 15px;
    }

    [class^="icon-"] {
        font-size: 2.8rem;
        margin: 0 0 0 10px;
    }

    .icon-checked {
        color: $orange;
    }

    &__inprogress-courses {
        margin: 20px 0 0 0;
    }
}
</style>
